// a library to wrap and simplify api calls
import axios from 'axios'
import history from '../history'
import config from '../Config'
import logger from '../../Common/Utils/Logger'
// import { refreshJwt } from '../../Auth/Services'
import * as R from 'ramda'
// rtk
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { logout } from '../../Auth/Redux/Auth'
import { authTokenRequest, msalConfig } from '../../Auth/Config'
import storageFactory from '../../Common/Services/Storage'
import { defaultIfEmpty, defaultIfNilOrEmpty } from '../../Common/Utils/Functional'
import { refreshToken, selectAccount } from '../../Auth/Redux/Auth'
import { PublicClientApplication } from '@azure/msal-browser'
import store from '../Redux/Store'

export const msalInstance = new PublicClientApplication(msalConfig)

const axiosApi = axios.create({
  baseURL: config.apiBasePath,
  // here are some default headers
  headers: {
    Accept: 'application/json',
  },
  timeout: 0,
})

// Add a response interceptor
axiosApi.interceptors.response.use(
  (response) => {
    if (response.config.avoidInterceptors) {
      return response // file download, we need headers
    }
    return response.data // 200
  },
  (error) => {
    logger.warning('Failed request: ', error.config, error.response)
    if (error.config && error.response && error.response.status === 401) {
      logger.info('Getting unauthorized response, token expired? Refreshing it.')
      return refreshJwt().then((token) => {
        logger.debug('Token succesfully refreshed: ', token)
        if (error.config.data) error.config.data = JSON.parse(error.config.data)
        error.config.headers.Authorization = `Bearer ${token}`
        logger.info('Performing request again: ', error.config)
        return axios.request(error.config).then(
          (response) => {
            logger.info('Ok, refresh succesfull, requets completed')
            return response.data
          },
          (error) => {
            logger.warning('Request failed again after refresh :(', error)
            return error.response
          },
        )
      })
    } else if (error.response) {
      // response with error status code
      return error.response
    } else {
      // The request wasn't made or was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      logger.warning('Network error, redirecting to ', config.urls.networkError, error)
      setTimeout(() => history.push(config.urls.networkError), 0) // @TODO
    }
  },
)

const apiEndpoints = {
  // Authentication stuff
  setAuthToken: (token) => {
    axiosApi.defaults.headers.common.Authorization = `Bearer ${token}`
  },
  removeAuthToken: () => {
    delete axiosApi.defaults.headers.common.Authorization
  },
  users: {
    get: (userName) => axiosApi.get(`domain/user/${userName}`),
  },
  chatbot: {
    request: (body) => axiosApi.post('domain/chatbot', body),
  },
  // history
  history: {
    vehicles: {
      get: (qs) => axiosApi.get(`domain/operationlog/vehicleLogs?${apiQueryString(qs)}`), // export
    },
  },
  digitalTwin: {
    modelSignalOverviewData: {
      post: (body) => axiosApi.post('domain/digitaltwin/model/detail/signal/overview', body),
    },
    modelSignalValues: {
      get: (body) => axiosApi.post('domain/digitaltwin/model/detail/signal/values', body),
    },
    uploadModel: (formData, onProgress) =>
      axiosApi.post('domain/digitaltwin/models', formData, {
        onUploadProgress: onProgress,
      }),
  },
  testCells: {
    testRevisionDeviceUsage: {
      get: (deviceSerialNumber) => axiosApi.get(`domain/testrevisions/device/usage/${deviceSerialNumber}`),
    },
  },
  // applications data
  data: {
    applicationFiles: {
      get: (qs) => {
        const body = {
          top: qs.top,
          skip: qs.skip,
          orderType: qs.orderby,
          isDescending: qs.ordertype === 'desc',
          applications: R.defaultTo([], qs.applications),
          fromReceivedAt: defaultIfEmpty(undefined, qs.fromReceivedAt),
          toReceivedAt: defaultIfEmpty(undefined, qs.toReceivedAt),
          fromTimestamp: defaultIfEmpty(undefined, qs.fromTimestamp),
          toTimestamp: defaultIfEmpty(undefined, qs.toTimestamp),
          deviceIds: qs.deviceIds,
          vehicleIds: qs.vehicleIds,
          hasError: qs.hasError,
          processed: qs.processed,
          parsed: qs.parsed,
          archived: qs.archived,
          applicationFileId: qs.id,
          searchText: qs.search,
          signalTags: R.defaultTo([], qs.tags),
          ingestionStatus: defaultIfNilOrEmpty(undefined, qs.dapIngestion),
          reprocessStatus: defaultIfNilOrEmpty(undefined, qs.reprocessStatus),
        }
        return axiosApi.post(`domain/data/applicationfiles/get`, body)
      },
    },
    canLoggerFiles: {
      get: (qs) => {
        const body = {
          top: qs.top,
          skip: qs.skip,
          orderBy: qs.orderby,
          orderType: qs.ordertype,
          fromDatetime: defaultIfEmpty(undefined, qs.fromDatetime),
          toDatetime: defaultIfEmpty(undefined, qs.toDatetime),
          deviceIds: qs.deviceIds,
          vehicleIds: qs.vehicleIds,
          applicationFileId: qs.applicationFileId,
          reprocessConfigurationName: qs.reprocessConfigurationName,
          reprocessConfigurationVersion: qs.reprocessConfigurationVersion,
          configurationName: qs.configurationName,
          configurationVersion: qs.configurationVersion,
          loggerName: qs.loggerName,
          searchText: qs.search,
          isTelemaco: qs.isTelemaco,
          vehicleName: qs.vehicleName,
          deviceSerialNumber: qs.deviceSerialNumber,
          reprocessStatus: defaultIfNilOrEmpty(undefined, qs.reprocessStatus),
        }

        if (R.not(R.isNil(qs.hasAudioFile))) body.hasAudioFile = qs.hasAudioFile
        if (R.not(R.isNil(qs.isDeleted))) body.isDeleted = qs.isDeleted

        return axiosApi.post(`domain/data/canloggerfiles`, body) // export
      },
      downloadAudio: (id) =>
        axiosApi.get(`domain/data/canloggerfiles/${id}/download/audio`, {
          avoidInterceptors: true,
          responseType: 'blob',
          observe: 'response',
          headers: {
            Accept: '*/*',
          },
        }), // download file
    },
    signalLoggerFiles: {
      get: (qs) => axiosApi.get(`domain/data/signalloggerfiles?${apiQueryString(qs)}`), // export
      downloadAudio: (id) =>
        axiosApi.get(`domain/data/signalloggerfiles/${id}/download/audio`, {
          avoidInterceptors: true,
          responseType: 'blob',
          observe: 'response',
          headers: {
            Accept: '*/*',
          },
        }), // download file
    },
    rtmTrips: {
      get: (qs) => axiosApi.get(`domain/data/rtmTrip?${apiQueryString(qs)}`), // export
    },
    rtmTripsSignalValues: {
      get: (tripInstanceId, deviceDimId, startedOnYearMonthDimId, stoppedOnYearMonthDimId, signalDisplayNameId) =>
        axiosApi.get(
          `domain/data/rtmTripReportSignalValues?tripInstanceId=${tripInstanceId}&deviceDimId=${deviceDimId}&startedOnYearMonthDimId=${startedOnYearMonthDimId}&stoppedOnYearMonthDimId=${stoppedOnYearMonthDimId}&signalDisplayNameId=${signalDisplayNameId}`,
        ),
    },
    rtmTripsGpsValues: {
      get: (tripInstanceId, deviceDimId, startedOnYearMonthDimId, stoppedOnYearMonthDimId) =>
        axiosApi.get(
          `domain/data/rtmTripReportGpsValues?tripInstanceId=${tripInstanceId}&deviceDimId=${deviceDimId}&startedOnYearMonthDimId=${startedOnYearMonthDimId}&stoppedOnYearMonthDimId=${stoppedOnYearMonthDimId}`,
        ),
    },
    rtmEvents: {
      get: (qs) => axiosApi.get(`domain/data/rtmEvent?${apiQueryString(qs)}`), // export
    },
    rtmEventsSignalValues: {
      get: (eventInstanceId, deviceDimId, registeredOnYearMonthDimId, signalDisplayNameId) =>
        axiosApi.get(
          `domain/data/rtmEventReportSignalValues?eventInstanceId=${eventInstanceId}&deviceDimId=${deviceDimId}&registeredOnYearMonthDimId=${registeredOnYearMonthDimId}&signalDisplayNameId=${signalDisplayNameId}`,
        ),
    },
    rtmEventsGpsValues: {
      get: (eventInstanceId, deviceDimId, registeredOnYearMonthDimId) =>
        axiosApi.get(
          `domain/data/rtmEventReportGpsValues?eventInstanceId=${eventInstanceId}&deviceDimId=${deviceDimId}&registeredOnYearMonthDimId=${registeredOnYearMonthDimId}`,
        ),
    },
    rtmDtcs: {
      get: (qs) => axiosApi.get(`domain/data/rtmDtc?${apiQueryString(qs)}`), // export
    },
    miniVpr: {
      get: (qs) => axiosApi.get(`domain/data/minivpr?${apiQueryString(qs)}`), // export
    },
    missionProfile: {
      get: (qs) => axiosApi.get(`domain/data/missionprofile/sessions?${apiQueryString(qs)}`), // export
    },
    vehicleDiagnostic: {
      get: (qs) => axiosApi.get(`domain/data/vehiclediagnosticfiles?${apiQueryString(qs)}`), // export
    },
    infoReader: {
      get: (qs) => axiosApi.get(`domain/data/inforeaderfiles?${apiQueryString(qs)}`), // export
    },
    tdmsFiles: {
      get: (qs) => axiosApi.get(`domain/data/tdmsfiles?${apiQueryString(qs)}`), // export
    },
    downloadParsedFile: (id) =>
      axiosApi.get(`domain/data/downloadParsedFile?id=${id}`, {
        avoidInterceptors: true,
        responseType: 'blob',
        observe: 'response',
        headers: {
          Accept: '*/*',
        },
      }),
    downloadParsedFiles: (ids) => {
      return axiosApi.post('domain/data/downloadParsedFiles', { ids })
    },
    downloadRawFile: (id) =>
      axiosApi.get(`domain/data/downloadRawFile?id=${id}`, {
        avoidInterceptors: true,
        responseType: 'blob',
        observe: 'response',
        headers: {
          Accept: '*/*',
        },
      }),
    downloadRawFiles: (ids) => axiosApi.post('domain/data/downloadRawFiles', { ids }),
    downloadBlob: (url) => {
      const or = axiosApi.defaults.headers.common.Authorization
      delete axiosApi.defaults.headers.common.Authorization
      const res = axiosApi.get(url, {
        avoidInterceptors: true,
        responseType: 'blob',
        observe: 'response',
        headers: {
          Accept: '*/*',
        },
      })
      axiosApi.defaults.headers.common.Authorization = or
      return res
    },
    downloadRtmTrip: (body) => axiosApi.post('domain/data/rtmTrip/download', body),
    downloadRtmEvent: (body) => axiosApi.post('domain/data/rtmEvent/download', body),
    getUploadUrl: (deviceSerialNumber, fileNames) =>
      axiosApi.post('domain/data/applicationfiles/getuploadurl', {
        deviceSerialNumber,
        fileNames,
      }),
    processManualUpload: (body) => axiosApi.post('domain/data/applicationfiles/processupload', body),
    getDocumentsUploadUrl: (fileNames) =>
      axiosApi.post('domain/data/documents/getuploadurl', {
        fileNames,
      }),
    processDocumentsManualUpload: (body) => axiosApi.post('domain/data/documents/processupload', body),
    downloadDocuments: (ids) => axiosApi.post('domain/data/documents/download', { ids }),
    downloadImageBundle: (body) => axiosApi.post(`domain/data/imagebundles/download/`, body),
    getDownloadImageBundleUrl: (jobId) => axiosApi.get(`domain/data/imagebundles/downloadurl/${jobId}`),
    downloadBundleImage: (id) =>
      axiosApi.get(`domain/data/imageloggerdata/download/${id}`, {
        avoidInterceptors: true,
        responseType: 'blob',
        observe: 'response',
        headers: {
          Accept: '*/*',
        },
      }),
    imageBundleSuperviselyUrl: (id) => axiosApi.get(`domain/data/imagebundles/${id}/superviselyurl`),
    downloadBundleImages: (body) => {
      return axiosApi.post('domain/data/imageloggerdata/download', body)
    },
    downloadSuperviselyImages: (body) => {
      return axiosApi.post('domain/data/imageloggerdata/supervisely/download', body)
    },
    downloadImageDatasets: (body) => {
      return axiosApi.post('domain/data/imageLoggerDataset/download', body)
    },
  },
  dataAnalytics: {
    eventSignalOverviewValues: {
      get: (body) => axiosApi.post('domain/data/dap/event/getsignaloverviewdata', body),
    },
    eventSignalValues: {
      get: (body) => axiosApi.post('domain/data/dap/event/getsignaldata', body),
    },
    eventGpsValues: {
      get: (body) => axiosApi.post('domain/data/dap/event/getgpsdata', body),
    },
  },
  // signalr
  signalr: {
    upload: (segmentId) => axiosApi.post(`resource/resource/messages/${segmentId}`),
    configurations: (segmentId) => axiosApi.post(`configurator/configuration/messages/${segmentId}`),
    package: (groupId) => axiosApi.get(`domain/operationgroup/${groupId}/signalr`),
    dapjobs: () => axiosApi.get(`domain/data/dap/jobssignalr`),
    dapreports: () => axiosApi.get(`domain/data/dap/report/signalr`),
    dapexports: () => axiosApi.get(`domain/data/dap/exportjobssignalr`),
    frecDbcUpload: (specificationId) => axiosApi.post(`frec/specification/${specificationId}/dbc/messages`),
    digitaltwin: () => axiosApi.get(`domain/digitaltwin/signalr`),
    bundleCommand: (segmentId) => axiosApi.get(`domain/data/imagebundlesjobs/signalr/${segmentId}`),
    ftaoAggregations: () => axiosApi.get(`ftao/rgplan/signalr`),
    digitalTwinInstances: () => axiosApi.get(`domain/digitaltwin/instances/signalr`),
  },
  // resources
  resources: {
    get: () => axiosApi.get('resource/resource'),
    create: (formData, onProgress) =>
      axiosApi.post('resource/resource', formData, {
        onUploadProgress: onProgress,
      }),
    patch: (id, data) => axiosApi.patch(`resource/resource/${id}`, data),
    updateBusType: (id, data) => axiosApi.patch(`resource/resource/busType/${id}`, data),
    delete: (id) => axiosApi.delete(`resource/resource/${id}`),
    download: (id) =>
      axiosApi.get(`resource/resource/download/${id}`, {
        avoidInterceptors: true,
        responseType: 'blob',
        observe: 'response',
        headers: {
          Accept: '*/*',
        },
      }),
    downloadMultiple: (ids) => {
      return axiosApi.post('resource/resource/downloadmultiple', { ids })
    },
    uploadMultiple: (formData, onProgress) =>
      axiosApi.post('resource/resource/uploadmultiple', formData, {
        onUploadProgress: onProgress,
      }),
  },
  resourcesTypes: {
    get: () => axiosApi.get('resource/resourcetype'),
  },
  resourcesJobs: {
    get: () => axiosApi.get('resource/resourcejob'),
    delete: (id) => axiosApi.delete(`resource/resourcejob/${id}`),
  },
  // configurator
  configurations: {
    get: (qs) => axiosApi.get(`configurator/configuration?${apiQueryString(qs)}`), // export
    getAllMessages: (id) => axiosApi.get(`configurator/configuration/${id}/messages`),
    getAllDtcs: (id) => axiosApi.get(`configurator/configuration/${id}/dtcs`), // dtc in loggers
    download: (configurationId) =>
      axiosApi.get(`configurator/configuration/${configurationId}/download`, {
        avoidInterceptors: true,
        responseType: 'blob',
        observe: 'response',
        headers: {
          Accept: '*/*',
        },
      }),
  },
  a2lSignalConfigurations: {
    upload: (type, formData, onProgress) =>
      axiosApi.post(`configurator/configuration/a2lsignalconfigurations/upload/${type}`, formData, {
        onUploadProgress: onProgress,
      }),
    getConflicts: (formData) =>
      axiosApi.post(`configurator/configuration/a2lsignalconfigurations/upload/conflicts`, formData),
  },
  packages: {
    get: () => axiosApi.get('configuration/package'),
    send: (packageId, data) =>
      axiosApi.post(`configuration/package/${packageId}/send`, {
        skipWarnings: false,
        ...data,
      }),
  },
  loggerConfigurations: {
    get: (configurationId) => axiosApi.get(`configurator/loggerconfiguration/${configurationId}`),
    create: (loggerConfiguration) => axiosApi.post('configurator/loggerconfiguration', loggerConfiguration),
    createOrUpdate: (loggerConfiguration, id = null) =>
      axiosApi[id ? 'patch' : 'post'](`configurator/loggerconfiguration${id ? '/' + id : ''}`, loggerConfiguration),
    delete: (loggerConfigurationId) => axiosApi.delete(`configurator/loggerconfiguration/${loggerConfigurationId}`),
  },
  canLoggerConfigurations: {
    getMessages: (loggerId) => axiosApi.get(`configurator/canloggerconfiguration/${loggerId}/getmessages`),
    addMessages: (loggerId, messagesConfiguration) =>
      axiosApi.post(`configurator/canloggerconfiguration/${loggerId}/addmessages`, messagesConfiguration),
    deleteMessages: (loggerId, messages) =>
      axiosApi.post(`configurator/canloggerconfiguration/${loggerId}/deletemessages`, messages),
  },
  signalLoggerConfigurations: {
    getSignals: (loggerId, configurationId) =>
      axiosApi.get(
        `configurator/signalloggerconfiguration/${loggerId}/getsignals?configurationId=${configurationId}`,
      ),
    addSignals: (loggerId, signalsConfiguration) =>
      axiosApi.post(`configurator/signalloggerconfiguration/${loggerId}/addsignals`, signalsConfiguration),
    updateSignal: (loggerId, signalConfiguration) =>
      axiosApi.post(
        `configurator/signalloggerconfiguration/updatesignalconfiguration/${loggerId}`,
        signalConfiguration,
      ),
    deleteSignals: (loggerId, signals) =>
      axiosApi.post(`configurator/signalloggerconfiguration/${loggerId}/deletesignals`, signals),
    uploadSignalLoggerFilters: (loggerConfigurationId, formData, onProgress) =>
      axiosApi.post(
        `configurator/signalloggerconfiguration/uploadsignalloggerfilters/${loggerConfigurationId}`,
        formData,
        { onUploadProgress: onProgress },
      ),
    getSignalLoggerFiltersConflicts: (formData) =>
      axiosApi.post(`configurator/signalloggerconfiguration/a2lsignalconfigurations/upload/conflicts`, formData),
  },
  // data
  rtmReports: {
    get: (reportName) => axiosApi.get(`domain/data/rtmReport?reportName=${reportName}`),
  },
  // archive
  deviceImages: {
    changelog: (deviceImageId) => axiosApi.get(`archive/archive/deviceimages/changelog/${deviceImageId}`),
    download: (deviceImageId) => axiosApi.get(`archive/archive/deviceimages/download/${deviceImageId}`),
    applicationPackageDownload: (deviceImageId) =>
      axiosApi.get(`archive/archive/deviceimages/application-package/download/${deviceImageId}`),
  },
  // FRec
  frec: {
    signals: {
      get: (qs) => axiosApi.get(`frec/signal?${apiQueryString(qs)}`), // export
    },
    tables: {
      get: (qs) => axiosApi.get(`frec/table?${apiQueryString(qs)}`), // export
    },
    items: {
      get: (qs) => axiosApi.get(`frec/item?${apiQueryString(qs)}`), // export
    },
    specifications: {
      get: (qs) => axiosApi.get(`frec/specification?${apiQueryString(qs)}`), // export
      changelog: ({ specificationId, ...qs }) =>
        axiosApi.get(`frec/specification/changelog/${specificationId}?${apiQueryString(qs)}`), // export
      uploadDbc: (formData, onProgress) =>
        axiosApi.post('frec/specification/dbc', formData, {
          onUploadProgress: onProgress,
        }),
      exportDbcs: (specificationId) => axiosApi.post(`frec/specification/${specificationId}/export/dbcs`),
      exportCsv: (specificationId) =>
        axiosApi.get(`frec/specification/${specificationId}/export/csv`, {
          avoidInterceptors: true,
          responseType: 'blob',
          observe: 'response',
          headers: {
            Accept: '*/*',
          },
        }),
    },
    data: {
      getApplicationFilesUploadUrl: (fileNames) =>
        axiosApi.post('frec/data/applicationfiles/uploadurls', {
          fileNames,
        }),
      processApplicationFilesUploads: (body) => axiosApi.post('frec/data/applicationfiles/processuploads', body),
      downloadRawFiles: (ids) => axiosApi.post('frec/data/applicationfiles/downloadraw', { ids }),
      downloadParsedFiles: (ids) => axiosApi.post('frec/data/applicationfiles/downloadparsed', { ids }),
    },
  },
  // misc
  cn: {
    get: () => axiosApi.get(atob('aHR0cHM6Ly9hcGkuY2h1Y2tub3JyaXMuaW8vam9rZXMvcmFuZG9t')),
  },
}

export default apiEndpoints

export const refreshJwt = async () => {
  const account = selectAccount(store.getState())
  logger.info('Refreshing JWT for account', account)
  return msalInstance
    .acquireTokenSilent({ ...authTokenRequest, account })
    .then(async (response) => {
      logger.info('JWT retrieved - silent')
      store.dispatch(refreshToken(response.accessToken))
      apiEndpoints.setAuthToken(response.accessToken)
      return response.accessToken
    })
    .catch(async (err) => {
      logger.info('JWT acquire token silent failed, going for popup', err)
      msalInstance
        .acquireTokenPopup(authTokenRequest)
        .then(async (response) => {
          logger.info('JWT retrieved - popup')
          store.dispatch(refreshToken(response.accessToken))
          apiEndpoints.setAuthToken(response.accessToken)
          return response.accessToken
        })
        .catch((err) => {
          logger.error('Get jwt error:', err)
          history.push(config.urls.serverError.replace(':error', 'token'))
        })
    })
}

const Storage = storageFactory()

let refreshTokenPromise
let refreshLocked = false // lock request retry while refreshing

const baseQuery = fetchBaseQuery({
  baseUrl: config.apiBasePath,
  prepareHeaders: (headers, { getState }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const token = getState().auth.token
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    return headers
  },
})

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions)

  if (result.error && result.error.status === 401) {
    logger.debug('API 401 detected', args)
    if (!refreshLocked) {
      logger.debug('API no one is refreshing -> acquiring lock and actually refreshing now', args)
      refreshLocked = true // acquire lock
      let resolveRefreshTokenPromise
      refreshTokenPromise = new Promise((resolve) => {
        resolveRefreshTokenPromise = resolve
      })
      let token
      try {
        token = await refreshJwt()
      } catch (e) {
        logger.error('TOKEN ERROR', e)
      }

      if (token) {
        logger.debug('API token refreshed', args)
        logger.debug('API credentials set', args)
        logger.debug('API releasing refresh lock', args)
        resolveRefreshTokenPromise()
        refreshLocked = false
      } else {
        logger.debug('API token refreshed error', token, args)
        refreshLocked = false
        api.dispatch(logout())
        Storage.remove('refreshToken')
        history.push(config.urls.login)
        resolveRefreshTokenPromise()
        return result
      }
    }

    logger.debug('API would like to run again request', args)
    if (refreshLocked) {
      logger.debug('API wait, someone is refreshing token', args)
      await refreshTokenPromise
      logger.debug('API wait for other refreshing: DONE', args)
    }

    logger.debug('API actually running again request', args)
    // retry the initial query
    result = await baseQuery(args, api, extraOptions)
    logger.debug('API request retry: DONE', args)
  }

  return result
}

export const configuratorTags = [
  'A2l',
  'A2lRaster',
  'A2lSignalConfiguration',
  'A2lProtocolSettings',
  'BlobStorageContainer',
  'BlueTagConfiguration',
  'BusConfiguration',
  'Configuration',
  'ConfigurationA2lSignal',
  'ConfigurationDtc',
  'ConfigurationDtcResource',
  'ConfigurationHistory',
  'ConfigurationMessage',
  'ConfigurationSignal',
  'DefaultEcuDataFileAndBus',
  'DigitalTwinAccumulation',
  'DigitalTwinConfiguration',
  'DigitalTwinModel',
  'Dm1Configuration',
  'Dm1Exception',
  'HwSettingsConfiguration',
  'HwSettingsKeepOnSource',
  'HwSettingsKeepOnSourceType',
  'HwSettingsKeepOnSourcesConfiguration',
  'HwSettingsLedFunction',
  'HwSettingsLedConfiguration',
  'HwSettingsLedSettings',
  'HwSettingsWakeUpSource',
  'HwSettingsWakeUpSourceType',
  'HwSettingsWakeUpSourcesConfiguration',
  'ImageLoggerCamera',
  'ImageLoggerTag',
  'InfoReaderConfiguration',
  'InfoReaderConfigurationSection',
  'InfoReaderConfigurationSignal',
  'InfoReaderSectionSignal',
  'InfoReaderSectionTrigger',
  'InfoReaderSendingMethod',
  'InsightConfiguration',
  'InsightTransmissionType',
  'InsightType',
  'InsightPgn',
  'Logger',
  'MiniVprConfiguration',
  'MiniVprConfigurationSignal',
  'MissionProfileAccumulation',
  'MissionProfileAggregation',
  'MissionProfileAggregationData',
  'MissionProfileAggregationSession',
  'MissionProfileConfiguration',
  'MissionProfileItem',
  'MissionProfileLimit',
  'RtmConfiguration',
  'RtmConfigurationEvent',
  'RtmConfigurationTrip',
  'SignalAlias',
  'Trigger',
  'TriggerTree',
]

export const apiTags = [
  ...configuratorTags,
  'AccumulationTimeUnit',
  'ApplicationFile',
  'ApplicationFileQueue',
  'ApplicationType',
  'BlobStorageList',
  'BlueTag',
  'blueTagAttachment',
  'BlueTagFilter',
  'BlueTagCustomerFarm',
  'BlueTagImplement',
  'BlueTagMission',
  'Bus',
  'BusType',
  'BusTypeAlias',
  'CanLoggerAudio',
  'CanLoggerFile',
  'Command',
  'Country',
  'Cts3ServiceSite',
  'Cts3SystemName',
  'Cts3TestCategory',
  'Cts3TestType',
  'CtsData',
  'CtsTestRevision',
  'DapReport',
  'DapReportInfo',
  'DapReportItem',
  'DapReportItemFile',
  'DataAnalyticsDtc',
  'DataAnalyticsDtcProtocolType',
  'DataAnalyticsExportJob',
  'DataAnalyticsQuery',
  'DataAnalyticsEventSignal',
  'DataAnalyticsRegistry',
  'DataAnalyticsSignal',
  'Device',
  'DeviceConfigSoftwareHistory',
  'DeviceFamily',
  'DevicesFleet',
  'DeviceImage',
  'DeviceDtModelAssociation',
  'DtModelSignal',
  'DeviceStatus',
  'DeviceType',
  'Dm1BusType',
  'Dm1FilterType',
  'Domain',
  'DtInstance',
  'DtModel',
  'DtModelSignal',
  'EmissionLevel',
  'EngineType',
  'ExternalUserTag',
  'FrecAccumulationType',
  'FrecApplicationData',
  'FrecApplicationFile',
  'FrecApplicationFileValidation',
  'FrecEcuType',
  'FrecFuelType',
  'FrecItem',
  'FrecItemSpecification',
  'FrecItemSpecificationStartByte',
  'FrecItemSuggestion',
  'FrecMeasureUnit',
  'FrecParsedFile',
  'FrecRole',
  'FrecSignal',
  'FrecSignalImpact',
  'FrecSignalType',
  'FrecSignalTypeMeasureUnit',
  'FrecSpecification',
  'FrecSpecificationChangelog',
  'FrecSpecificationDbc',
  'FrecSpecificationDbcExport',
  'FrecSpecificationDbcSignal',
  'FrecSpecificationInfo',
  'FrecSpecificationItem',
  'FrecSpecificationItemSuggestion',
  'FrecSpecificationTableAlias',
  'FrecSpecificationValidationRule',
  'FrecSubSystem',
  'FrecSystem',
  'FrecTable',
  'FrecTableCanSignal',
  'FrecTableSuggestion',
  'FrecTransmissionType',
  'FrecVehicle',
  'FtaoRgPlan',
  'FtaoRgPlanAggregationItemData',
  'FtaoRgPlanEvents',
  'FtaoRgPlanFingerprintItemData',
  'FtaoRgPlanFingerprintProgram',
  'FtaoRgPlanFingerprintReport',
  'FtaoRgPlanItem',
  'FtaoRgPlanPowerBi',
  'FtaoRgPlanSpecification',
  'FtaoRgPlanSpecificationItem',
  'FtaoRgPlanSuggestions',
  'FtaoRgPlanSuggestionsEvents',
  'FtaoRgPlanSuggestionsHistory',
  'FtaoRgPlanSuggestionsHistoryData',
  'FtaoRgPlanTargetHistory',
  'FtaoRgPlanTargetItemData',
  'FtaoRgPlanTargetSimilarItems',
  'FtaoRgPlanVehicle',
  'FtaoRole',
  'GpdPhase',
  'ImageBundle',
  'ImageBundleJob',
  'ImageDataset',
  'ImageDatasetImage',
  'ImageLoggerData',
  'ImageSuperviselyBundle',
  'InfoReaderData',
  'InfoReaderFile',
  'InfoReaderSectionBoardData',
  'InfoReaderSectionSendingMethod',
  'InfoReaderSnapshot',
  'LinkedDocument',
  'LoggerCalculationType',
  'LoggerMaxSizeType',
  'LoggerOutputFormat',
  'LoggerTimelineType',
  'LoggerValueType',
  'MiniVprAccumulation',
  'MiniVprConditionType',
  'MiniVprData',
  'MiniVprFile',
  'MissionProfileAccumulationType',
  'MissionProfileData',
  'MissionProfileSession',
  'MissionProfileStatus',
  'Model',
  'Network',
  'OperationLogGroup',
  'Platform',
  'ProductLine',
  'Program',
  'PvResponsible',
  'ReprocessMessage',
  'ResourceCanSignal',
  'Role',
  'RtmEvent',
  'RtmEventAudio',
  'RtmEventReportSignal',
  'RtmEventType',
  'RtmRawFile',
  'RtmReport',
  'RtmReportSignal',
  'RtmDtc',
  'RtmEvent',
  'RtmTrip',
  'RtmTripPart',
  'Segment',
  'SignalLoggerAudio',
  'SignalLoggerFile',
  'SignalType',
  'SimInfo',
  'Site',
  'StandaloneDocument',
  'SuperviselyBundleImage',
  'SuperviselyBundleUrl',
  'SuperviselyImageAnnotation',
  'TdmsFile',
  'TestCellFacility',
  'TestCellFacilityDevice',
  'TestCellFacilityVehicle',
  'TestRevision',
  'TriggerNodeType',
  'TriggerSignalNodeEdge',
  'TriggerSignalNodeFilter',
  'TriggerStatus',
  'User',
  'Vehicle',
  'VehicleComponent',
  'VehicleComponentType',
  'VehicleConfigSoftwareHistory',
  'VehicleDiagnosticData',
  'VehicleDiagnosticFile',
  'VehicleEcuSoftwareVersion',
  'FieldConnectInsightToEcuSWVersionDatas',
  'VehiclesFleet',
  'VehicleHistory',
  'VehicleDtModelAssociation',
  'VehicleStatus',
]

// https://redux-toolkit.js.org/rtk-query/usage/code-splitting
export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: apiTags,
  endpoints: () => ({}),
})

export const apiList = (res) => ({
  ...res,
  orData: res.data,
  data: res.data?.data?.value || [],
})
export const apiDictFromList = (res, idx = 0, dft = {}) => ({
  ...res,
  orData: res.data,
  data: res.data?.data?.value?.[idx] || dft,
})
export const apiDict = (res, dft = {}) => ({
  ...res,
  orData: res.data,
  data: res.data?.data && R.not(R.isEmpty(res.data.data)) ? res.data.data : dft,
})
export const apiQueryString = (qs) => {
  const { top, skip, orderby, ordertype, ...rest } = qs
  const more = Object.keys(rest)
    .filter(R.compose(R.not, R.either(R.isEmpty, R.isNil), R.flip(R.prop)(rest)))
    .map((k) => `${k}=${encodeURIComponent(rest[k])}`)

  return `top=${top}&skip=${skip}&orderby=${orderby}&ordertype=${ordertype}` + (more.length ? '&' + more.join('&') : '')
}
export const apiDictToQueryString = (qs) => {
  const more = Object.keys(qs)
    .filter(R.compose(R.not, R.either(R.isEmpty, R.isNil), R.flip(R.prop)(qs)))
    .map((k) => `${k}=${encodeURIComponent(qs[k])}`)

  return more.join('&')
}
